import React from 'react';
import { Button, CircularProgress, type ButtonProps } from '@mui/material';

export type PillCtaVariant = 'primary' | 'outline' | 'secondary';
export type PillCtaSize = 'md' | 'lg';

export interface PillCtaButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
  /** Visual variant: filled primary, outlined brand, or outlined neutral */
  variant?: PillCtaVariant;
  /** Size: md (form footer) or lg (card CTA) */
  size?: PillCtaSize;
  /** Test ID for automated testing */
  testId?: string;
  /** Children/button content */
  children: React.ReactNode;
  /** When true, replaces button text with a spinner and disables the button */
  isLoading?: boolean;
}

const SIZE_STYLES: Record<PillCtaSize, { py: string; fontSize: string; lineHeight: number }> = {
  lg: { py: '20px', fontSize: '18px', lineHeight: 1.3 },
  md: { py: '10px', fontSize: '16px', lineHeight: 1.3 },
};

/**
 * PillCtaButton - Generic pill-shaped CTA button
 *
 * Atomic Level: Atom
 *
 * A full-width pill button for primary call-to-action contexts.
 * No domain knowledge — receives all content and callbacks via props.
 *
 * Variants:
 * - `primary`: Filled with theme primary color (e.g. "Request Info", "Next")
 * - `outline`: Outlined with theme primary color (e.g. "Book a Tour")
 * - `secondary`: Outlined neutral style (e.g. inline utility actions)
 *
 * Sizes:
 * - `lg`: Large pill for card-level CTAs (18px, py 20px)
 * - `md`: Medium pill for form footers (16px, py 10px)
 *
 * @example
 * ```tsx
 * <PillCtaButton variant="primary" size="lg" onClick={handleClick}>
 *   Request Info
 * </PillCtaButton>
 *
 * <PillCtaButton variant="outline" size="lg" onClick={handleClick}>
 *   Book a Tour
 * </PillCtaButton>
 * ```
 */
const SPINNER_SIZE: Record<PillCtaSize, number> = {
  lg: 22,
  md: 18,
};

export function PillCtaButton({
  variant = 'primary',
  size = 'md',
  testId,
  children,
  isLoading = false,
  sx,
  disabled,
  ...buttonProps
}: PillCtaButtonProps) {
  const { py, fontSize, lineHeight } = SIZE_STYLES[size];
  const isPrimary = variant === 'primary';
  const isSecondary = variant === 'secondary';

  return (
    <Button
      variant={isPrimary ? 'contained' : 'outlined'}
      fullWidth
      disabled={disabled}
      aria-busy={isLoading}
      data-testid={testId}
      sx={{
        borderRadius: '100px',
        py,
        fontSize,
        lineHeight,
        fontWeight: 700,
        textTransform: 'none',
        ...(isSecondary
          ? {
            backgroundColor: '#fff',
            borderColor: 'transparent',
            color: '#1a1a1a',
            '&:hover': {
              backgroundColor: '#fff',
              borderColor: 'transparent',
            },
          }
          : {}),
        ...sx,
      }}
      {...buttonProps}
    >
      {isLoading ? (
        <CircularProgress
          size={SPINNER_SIZE[size]}
          color="inherit"
          aria-label="Loading"
        />
      ) : children}
    </Button>
  );
}

export default PillCtaButton;
